import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowLeft, PlayCircle, Printer } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const flow: {
  to: string;
  label: string;
  tag: string;
  onePager?: { to: string; label: string };
}[] = [
  { to: "/schools", label: "Principal Overview", tag: "Executive lens" },
  {
    to: "/schools/student",
    label: "Student Profile",
    tag: "Zoom into a learner",
    onePager: { to: "/schools/student/onepager", label: "Learner one-pager" },
  },
  {
    to: "/schools/parent",
    label: "Parent Dashboard",
    tag: "Family engagement",
    onePager: { to: "/schools/parent/onepager", label: "Family one-pager" },
  },
  {
    to: "/schools/counsellor",
    label: "Counsellor",
    tag: "Prioritised caseload",
    onePager: { to: "/schools/counsellor/onepager", label: "Weekly brief" },
  },
  { to: "/schools/studios", label: "Learning Studios", tag: "WALS™ · WAFLE™ · WAFFS™" },
  { to: "/schools/portfolio", label: "Portfolio", tag: "Digital learner profile" },
  { to: "/schools/reports", label: "Reports", tag: "Share the story" },
];

export default function DemoFlow() {
  const { pathname } = useLocation();
  const idx = Math.max(
    0,
    flow.findIndex((s) => (s.to === "/schools" ? pathname === s.to : pathname.startsWith(s.to)))
  );
  const current = flow[idx];
  const prev = idx > 0 ? flow[idx - 1] : null;
  const next = idx < flow.length - 1 ? flow[idx + 1] : null;
  const step = idx + 1;

  return (
    <Card className="border-dashed bg-gradient-to-br from-primary/5 via-background to-background">
      <div className="p-4 md:p-5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
            <PlayCircle className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="rounded-full text-[10px]">Demo flow · Step {step} of {flow.length}</Badge>
            </div>
            <div className="text-sm font-medium mt-1 truncate">{current.label}</div>
          </div>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2 flex-wrap">
          {current.onePager && (
            <Button asChild variant="outline" size="sm" className="no-print">
              <Link to={current.onePager.to} aria-label={`Open printable ${current.onePager.label}`}>
                <Printer className="w-3.5 h-3.5 mr-1" /> {current.onePager.label}
              </Link>
            </Button>
          )}
          {prev && (
            <Button asChild variant="ghost" size="sm">
              <Link to={prev.to}><ArrowLeft className="w-3.5 h-3.5 mr-1" /> {prev.label}</Link>
            </Button>
          )}
          {next && (
            <Button asChild size="sm">
              <Link to={next.to}>Next: {next.label} <ArrowRight className="w-3.5 h-3.5 ml-1" /></Link>
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
